/**
 * O vocabulário do painel — **o contrato entre a tela e `docs/product/metricas.md`**.
 *
 * Cada número que aparece no painel tem uma entrada aqui, e cada entrada declara três coisas:
 *
 *  - `label`: como o número se chama na tela;
 *  - `fonte`: **onde ele está definido** em `metricas.md`. É o critério de aceite da Fase 6
 *    escrito em código: um número novo sem seção é um número que alguém inventou, e o
 *    typecheck não deixa criar a entrada sem preencher este campo;
 *  - `semDados`: a frase que substitui o travessão quando o valor é `null`. Nunca "0", nunca
 *    "—" mudo: o vazio explica o que falta acontecer, senão ele é lido como defeito.
 *
 * Nada aqui calcula (invariante 3). É texto e classificação — comparar dois números que já
 * vieram prontos do servidor, no máximo.
 */
import {
  ArrowDownToLine,
  TrendingDown,
  ArrowLeftRight,
  ArrowUpFromLine,
  DoorOpen,
  Eye,
  Hourglass,
  MousePointerClick,
  Percent,
  Target,
  Users,
} from 'lucide-react';
import type { ComponentType, SVGProps } from 'react';

import type { MetricFormat, MetricPolarity } from '@mgdlead/ui';

export interface MetricaSpec {
  label: string;
  /** A seção de `docs/product/metricas.md` que define este número. Obrigatório. */
  fonte: string;
  /** O que a tela diz quando o servidor manda `null`. */
  semDados: string;
  /** O que "mais" significa. Nunca "verde = subiu". */
  polarity: MetricPolarity;
  format?: MetricFormat;
  abbreviate?: 'auto' | 'never';
  /** Uma linha de explicação, para o tooltip do rótulo. */
  ajuda?: string;
  /**
   * O ícone do tile (referência que o dono trouxe em 19/09).
   *
   * Mora no CATÁLOGO, e não em cada tela, porque é assim que a mesma métrica fica com a
   * mesma cara em todo lugar onde aparece — o ícone vira parte do vocabulário, e o olho
   * passa a achar "Entradas" pela forma antes de ler o rótulo. É âncora visual, nunca
   * informação: ele é `aria-hidden`, e nada que ele sugira deixa de estar escrito.
   */
  icon?: ComponentType<SVGProps<SVGSVGElement>>;
}

/**
 * Por que tantas frases diferentes de "sem dados": porque elas não querem dizer a mesma
 * coisa. "Nenhum monitorador conectado", "nenhum clique ainda" e "nenhuma saída com entrada
 * registrada" mandam a pessoa fazer três coisas diferentes. Uma frase genérica manda fazer
 * nada — e aí o vazio vira o defeito que ele parecia ser.
 */
export const METRICA = {
  saldo: {
    icon: ArrowLeftRight,
    label: 'Saldo no período',
    fonte: 'metricas.md §1 — Saldo = Entradas − Saídas',
    semDados: 'Ainda não houve nenhum evento neste workspace. Isso é diferente de saldo zero.',
    polarity: 'higher-is-better',
    abbreviate: 'never',
    ajuda:
      'Entradas menos saídas voluntárias. Remoção feita por admin não entra: ela tem linha própria, e a campanha não a causou.',
  },
  entradas: {
    icon: ArrowDownToLine,
    label: 'Entradas',
    fonte: 'metricas.md §1 — Entradas = count(JOIN)',
    semDados: 'Ainda não houve nenhum evento neste workspace.',
    polarity: 'higher-is-better',
    abbreviate: 'never',
    ajuda: 'Não mede quem entrou e saiu antes de o aviso chegar até nós.',
  },
  saidas: {
    icon: ArrowUpFromLine,
    label: 'Saídas',
    fonte: "metricas.md §1 — Saídas = count(LEAVE com causa 'self')",
    semDados: 'Ainda não houve nenhum evento neste workspace.',
    polarity: 'lower-is-better',
    abbreviate: 'never',
    ajuda:
      'Só quem saiu por conta própria. Saída de causa desconhecida não entra: tratá-la como voluntária inventaria evasão que não houve.',
  },
  visitasPagina: {
    icon: Eye,
    label: 'Visitas na página de ofertas',
    fonte: 'metricas.md §3 — visitas na LP (landing_page_view da Meta)',
    semDados:
      'A Meta ainda não contou visitas: falta uma campanha da Meta vinculada a um bloco, com a página carregando o pixel.',
    polarity: 'higher-is-better',
    abbreviate: 'never',
    ajuda:
      'Quantas vezes a página de ofertas abriu, contadas pela Meta. Só entram as campanhas vinculadas a um bloco.',
  },
  taxaCliquePagina: {
    icon: Percent,
    label: 'Cliques na página',
    fonte: 'metricas.md §3 — taxa de clique da LP = cliques ÷ visitas na LP',
    semDados: 'Sem visitas contadas pela Meta no período, não há taxa.',
    polarity: 'higher-is-better',
    format: 'percent',
    ajuda:
      'De cada 100 visitas na página de ofertas, quantas clicaram no botão do grupo. É a nota da PÁGINA: se cair, mexa na página, não no anúncio.',
  },
  cliques: {
    icon: MousePointerClick,
    label: 'Cliques',
    fonte: 'metricas.md §3 — cliques(T)',
    semDados: 'Nenhum redirecionador recebeu clique ainda.',
    polarity: 'higher-is-better',
    abbreviate: 'never',
  },
  cliquesUnicos: {
    icon: Users,
    label: 'Visitantes distintos',
    fonte: 'metricas.md §3 — cliques_únicos(T)',
    semDados: 'Nenhum redirecionador recebeu clique ainda.',
    polarity: 'higher-is-better',
    abbreviate: 'never',
  },
  cobertura: {
    icon: Target,
    label: 'Cobertura de atribuição',
    fonte: 'metricas.md §3 — cobertura_atribuição = entradas atribuídas / Entradas',
    semDados:
      'Não houve entrada no período, então não há o que atribuir. Isso é diferente de 0% de cobertura.',
    polarity: 'higher-is-better',
    format: 'percent',
    ajuda:
      'De quanto do seu dado a gente sabe a origem. É saúde do SISTEMA, não desempenho de campanha: se cair, o problema é nosso.',
  },
  taxaEntrada: {
    icon: DoorOpen,
    label: 'Taxa de entrada',
    fonte: 'metricas.md §3 — taxa_de_entrada = entradas atribuídas / cliques únicos',
    semDados: 'Nenhum clique no período — sem denominador, não há taxa.',
    polarity: 'higher-is-better',
    format: 'percent',
    ajuda:
      'De cada 100 PESSOAS que clicaram no link, quantas entraram no grupo. Quem clicou duas vezes conta uma.',
  },
  meiaVida: {
    icon: Hourglass,
    label: 'Permanência (mediana)',
    fonte: 'metricas.md §4 — meia_vida(T) = mediana(permanência) entre os que já saíram',
    semDados:
      'Nenhuma saída do período tinha entrada registrada, então não dá para medir quanto tempo essas pessoas ficaram.',
    polarity: 'higher-is-better',
    abbreviate: 'never',
    ajuda:
      'Mediana, nunca média: a distribuição tem cauda longa e a média é puxada pelos veteranos.',
  },
  evasao: {
    icon: TrendingDown,
    label: 'Evasão do período',
    fonte: 'metricas.md §4 — Saídas / participantes no início do período',
    semDados: 'Este grupo nunca foi contado, então não há denominador.',
    polarity: 'lower-is-better',
    format: 'percent',
  },
} as const satisfies Record<string, MetricaSpec>;

export type MetricaId = keyof typeof METRICA;

/* ========================================================================== */
/*  Período                                                                    */
/* ========================================================================== */

export const PERIODOS = [
  { id: 'HOJE', label: 'Hoje' },
  { id: 'D7', label: '7 dias' },
  { id: 'D30', label: '30 dias' },
  { id: 'D90', label: '90 dias' },
] as const;

export type PeriodoId = (typeof PERIODOS)[number]['id'];

/** Intervalo escolhido no "📅 Datas" (dias do negócio, inclusive). */
export type Intervalo = { from: string; to: string };
/** O que as telas passam para as consultas: um preset OU um intervalo. */
export type Periodo = PeriodoId | Intervalo;

/** Rótulo curto do período, para micro-rótulos ("30 dias", "01/10–06/10"). */
export function rotuloDoPeriodo(p: Periodo): string {
  if (typeof p === 'string') return PERIODOS.find((x) => x.id === p)?.label ?? '';
  return p.from === p.to ? diaCurto(p.from) : `${diaCurto(p.from)}–${diaCurto(p.to)}`;
}

/* ========================================================================== */
/*  Cores das séries — fixas por ENTIDADE, nunca por posição                   */
/* ========================================================================== */

/**
 * "Entradas" é sempre violeta e "Saídas" é sempre âmbar, em todo o produto.
 *
 * A cor segue a entidade e não o lugar dela na lista: um filtro que tire uma série não pode
 * repintar as que sobraram, senão quem aprendeu "entradas são as roxas" passa a ler errado.
 *
 * O par foi conferido no verificador de paleta nos dois temas contra as superfícies reais:
 * ΔE 25,8 no pior caso de daltonismo (protanopia) e 28,1 na visão normal — folgado acima do
 * piso de 8. E mesmo assim há legenda e rótulo direto: cor nunca é o único canal.
 */
export const SERIE = {
  entradas: { color: 'var(--color-chart-1)', label: 'Entradas' },
  saidas: { color: 'var(--color-chart-2)', label: 'Saídas' },
} as const;

/* ========================================================================== */
/*  Formatação de apoio (não é cálculo: é texto)                               */
/* ========================================================================== */

/** `2026-03-10` -> `10/03`. Já vem no fuso do workspace, resolvido pelo servidor. */
export function diaCurto(businessDate: string): string {
  const [, mes = '', dia = ''] = businessDate.split('-');
  return `${dia}/${mes}`;
}

/** `2026-03-10` -> `10/03/2026`. */
export function diaLongo(businessDate: string): string {
  const [ano = '', mes = '', dia = ''] = businessDate.split('-');
  return `${dia}/${mes}/${ano}`;
}

/** `7` -> `07h`. O eixo do histograma — no fuso do workspace, nunca em UTC. */
export function horaCurta(hour: number): string {
  return `${String(hour).padStart(2, '0')}h`;
}

/**
 * Dias em texto. **Arredonda para exibir, não para calcular** — o número que veio do servidor
 * continua sendo a mediana exata, e é ele que vai para o CSV quando a Fase 9 chegar.
 */
export function diasEmTexto(dias: number): string {
  if (dias < 1) {
    const horas = Math.round(dias * 24);
    return horas === 1 ? '1 hora' : `${horas} horas`;
  }
  const inteiros = Math.round(dias);
  return inteiros === 1 ? '1 dia' : `${inteiros} dias`;
}
